// src/pages/AdminCreditsPage.jsx
// Admin Credit Conversion Policy Panel
// This is a CONTROL PLANE for credit conversion ratios, not a pricing page.
// Policies are append-only: view existing, create new (future-dated only), no edit/delete.

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  Coins, Clock, AlertCircle, CheckCircle2, History, Plus, Info, Shield, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import AdminNav from '@/components/AdminNav';
import AdminTopMenuBar from '@/components/AdminTopMenuBar';
import UniversalFooter from '@/components/UniversalFooter';
import { BACKEND_URL } from '@/config/api';

// BACKEND_URL imported from centralized config
const API = `${BACKEND_URL}/api`;

const AdminCreditsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [currentPolicy, setCurrentPolicy] = useState(null);
  const [scheduledPolicies, setScheduledPolicies] = useState([]);
  const [allPolicies, setAllPolicies] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  
  // Create form state
  const [newPolicy, setNewPolicy] = useState({
    aeu_to_credits_ratio: 8.0,
    peu_to_credits_ratio: 1.0,
    effective_start_timestamp: '',
    change_reason: ''
  });
  const [creating, setCreating] = useState(false);

  // Helper to get auth token (supports both token and accessToken keys)
  const getAuthToken = () => localStorage.getItem('token') || localStorage.getItem('accessToken');

  // Load policies on mount
  useEffect(() => {
    loadPolicies();
  }, []);

  const loadPolicies = async () => {
    try {
      setLoading(true);
      const token = getAuthToken();
      
      // Get current and scheduled policies
      const [currentRes, allRes] = await Promise.all([
        axios.get(`${API}/admin/credits/policies`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/credits/policies/all`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setCurrentPolicy(currentRes.data.current);
      setScheduledPolicies(currentRes.data.scheduled || []);
      setAllPolicies(allRes.data.policies || []);
    } catch (error) {
      console.error('Error loading policies:', error);
      toast.error('Failed to load credit policies');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePolicy = async (e) => {
    e.preventDefault();
    
    // Validate effective date is in the future
    const effectiveDate = new Date(newPolicy.effective_start_timestamp);
    const now = new Date();
    
    if (effectiveDate <= now) {
      toast.error('Effective date must be in the future. Policies cannot be backdated.');
      return;
    }
    
    try {
      setCreating(true);
      const token = getAuthToken();
      
      await axios.post(`${API}/admin/credits/policies`, {
        aeu_to_credits_ratio: parseFloat(newPolicy.aeu_to_credits_ratio),
        peu_to_credits_ratio: parseFloat(newPolicy.peu_to_credits_ratio),
        effective_start_timestamp: new Date(newPolicy.effective_start_timestamp).toISOString(),
        change_reason: newPolicy.change_reason || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Credit policy scheduled successfully');
      setShowCreateForm(false);
      setNewPolicy({
        aeu_to_credits_ratio: 8.0,
        peu_to_credits_ratio: 1.0,
        effective_start_timestamp: '',
        change_reason: ''
      });
      loadPolicies();
    } catch (error) {
      console.error('Error creating policy:', error);
      const message = error.response?.data?.detail || 'Failed to create policy';
      toast.error(message);
    } finally {
      setCreating(false);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return 'N/A';
    return new Date(isoString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-white">
        <AdminNav />
        <AdminTopMenuBar />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <AdminNav />
      <AdminTopMenuBar />
      
      <div className="pb-20 md:pb-0">
        {/* Header */}
        <header className="bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900 border-b border-slate-800">
          <div className="mx-auto max-w-7xl px-4 py-10 sm:py-12">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <Coins className="w-8 h-8 text-amber-500" />
                  <h1 className="text-3xl sm:text-4xl font-bold">Credit Conversion Policy</h1>
                </div>
                <p className="text-sm sm:text-base text-slate-300 max-w-2xl">
                  Manage how Engagement Units (AEU/PEU) convert to Reader Credits. 
                  Changes apply to future activity only.
                </p>
              </div>
              <button
                onClick={loadPolicies}
                className="self-start flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg border border-slate-600 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="mx-auto max-w-7xl px-4 py-8">
          {/* Important Notice */}
          <Card className="bg-blue-900/20 border-blue-700/50 mb-6">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-200">
                  <p className="font-medium mb-1">About Credit Conversion</p>
                  <p className="text-blue-300/80">
                    Reader Credits are derived from Engagement Units using these ratios. 
                    <strong> Changes apply to future activity only</strong> — historical data is not recalculated.
                    Policies cannot be edited or deleted once created.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Current Policy */}
          <Card className="bg-slate-900 border-slate-700 mb-6">
            <CardHeader>
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
                Current Active Policy
              </CardTitle>
              <CardDescription className="text-slate-400">
                Currently in effect for all credit calculations
              </CardDescription>
            </CardHeader>
          <CardContent>
            {currentPolicy ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <div className="text-xs text-slate-500 mb-1">AEU → Credits Ratio</div>
                  <div className="text-2xl font-bold text-amber-400">
                    {currentPolicy.aeu_to_credits_ratio}×
                  </div>
                  <div className="text-xs text-slate-400 mt-1">
                    1 AEU = {currentPolicy.aeu_to_credits_ratio} Credits
                  </div>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <div className="text-xs text-slate-500 mb-1">PEU → Credits Ratio</div>
                  <div className="text-2xl font-bold text-cyan-400">
                    {currentPolicy.peu_to_credits_ratio}×
                  </div>
                  <div className="text-xs text-slate-400 mt-1">
                    1 PEU = {currentPolicy.peu_to_credits_ratio} Credits
                  </div>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <div className="text-xs text-slate-500 mb-1">Version</div>
                  <div className="text-lg font-semibold text-white">
                    {currentPolicy.credits_version_id}
                  </div>
                  <div className="text-xs text-slate-400 mt-1">
                    Effective: {formatDate(currentPolicy.effective_start_timestamp)}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-slate-400 text-center py-4">
                No active policy found
              </div>
            )}
          </CardContent>
        </Card>

        {/* Scheduled Policies */}
        {scheduledPolicies.length > 0 && (
          <Card className="bg-slate-900 border-slate-700 mb-6">
            <CardHeader>
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-500" />
                Scheduled Future Policies
              </CardTitle>
              <CardDescription className="text-slate-400">
                These policies will take effect on their scheduled dates
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {scheduledPolicies.map((policy, idx) => (
                  <div 
                    key={policy.id || idx}
                    className="bg-amber-900/20 border border-amber-700/50 rounded-lg p-4"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-amber-300">
                          {policy.credits_version_id}
                        </div>
                        <div className="text-sm text-amber-200/70 mt-1">
                          AEU: {policy.aeu_to_credits_ratio}× | PEU: {policy.peu_to_credits_ratio}×
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-slate-400">Effective</div>
                        <div className="text-sm text-amber-400">
                          {formatDate(policy.effective_start_timestamp)}
                        </div>
                      </div>
                    </div>
                    {policy.change_reason && (
                      <div className="text-xs text-slate-400 mt-2 pt-2 border-t border-amber-700/30">
                        Reason: {policy.change_reason}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Actions */}
        <div className="flex gap-4 mb-6">
          <Button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Schedule New Policy
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowHistory(!showHistory)}
            className="border-slate-600 text-slate-300 hover:bg-slate-800"
          >
            <History className="w-4 h-4 mr-2" />
            {showHistory ? 'Hide History' : 'View Policy History'}
          </Button>
        </div>

        {/* Create Form */}
        {showCreateForm && (
          <Card className="bg-slate-900 border-slate-700 mb-6">
            <CardHeader>
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <Plus className="w-5 h-5 text-amber-500" />
                Schedule New Credit Policy
              </CardTitle>
              <CardDescription className="text-slate-400">
                Create a new conversion policy that will take effect on the specified future date
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Warning Banner */}
              <div className="bg-amber-900/30 border border-amber-700/50 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="font-medium text-amber-300">Applies to future activity only</p>
                    <p className="text-amber-200/70 mt-1">
                      This policy will only affect new engagement after the effective date. 
                      Historical credit calculations will not change.
                    </p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleCreatePolicy} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="aeu_ratio" className="text-slate-300">
                      AEU → Credits Ratio
                    </Label>
                    <Input
                      id="aeu_ratio"
                      type="number"
                      step="0.001"
                      min="0.001"
                      value={newPolicy.aeu_to_credits_ratio}
                      onChange={(e) => setNewPolicy({
                        ...newPolicy,
                        aeu_to_credits_ratio: e.target.value
                      })}
                      className="bg-slate-800 border-slate-600 text-white"
                      required
                    />
                    <p className="text-xs text-slate-500">
                      How many Credits per Active Engagement Unit (e.g., 0.125 = 8 AEUs per 1 RC)
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="peu_ratio" className="text-slate-300">
                      PEU → Credits Ratio
                    </Label>
                    <Input
                      id="peu_ratio"
                      type="number"
                      step="0.001"
                      min="0.001"
                      value={newPolicy.peu_to_credits_ratio}
                      onChange={(e) => setNewPolicy({
                        ...newPolicy,
                        peu_to_credits_ratio: e.target.value
                      })}
                      className="bg-slate-800 border-slate-600 text-white"
                      required
                    />
                    <p className="text-xs text-slate-500">
                      How many Credits per Passive Engagement Unit (e.g., 0.200 = 5 PEUs per 1 RC)
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="effective_date" className="text-slate-300">
                    Effective Date & Time
                  </Label>
                  <Input
                    id="effective_date"
                    type="datetime-local"
                    value={newPolicy.effective_start_timestamp}
                    onChange={(e) => setNewPolicy({
                      ...newPolicy,
                      effective_start_timestamp: e.target.value
                    })}
                    min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
                    className="bg-slate-800 border-slate-600 text-white"
                    required
                  />
                  <p className="text-xs text-slate-500">
                    Must be a future date. Policy will apply to all activity after this time.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="reason" className="text-slate-300">
                    Change Reason (Optional)
                  </Label>
                  <Textarea
                    id="reason"
                    value={newPolicy.change_reason}
                    onChange={(e) => setNewPolicy({
                      ...newPolicy,
                      change_reason: e.target.value
                    })}
                    placeholder="Why is this policy being created?"
                    className="bg-slate-800 border-slate-600 text-white"
                    rows={2}
                  />
                </div>

                <div className="flex gap-4">
                  <Button
                    type="submit"
                    disabled={creating}
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                  >
                    {creating ? 'Creating...' : 'Schedule Policy'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowCreateForm(false)}
                    className="border-slate-600 text-slate-300 hover:bg-slate-800"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Policy History */}
        {showHistory && (
          <Card className="bg-slate-900 border-slate-700">
            <CardHeader>
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <History className="w-5 h-5 text-slate-400" />
                Policy History
              </CardTitle>
              <CardDescription className="text-slate-400">
                Complete audit trail of all credit conversion policies (read-only)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2 mb-4 text-xs text-slate-500">
                <Shield className="w-4 h-4" />
                <span>Policies cannot be edited or deleted once created</span>
              </div>
              
              {allPolicies.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-700">
                        <th className="text-left py-3 px-4 text-slate-400 font-medium">Version</th>
                        <th className="text-left py-3 px-4 text-slate-400 font-medium">AEU Ratio</th>
                        <th className="text-left py-3 px-4 text-slate-400 font-medium">PEU Ratio</th>
                        <th className="text-left py-3 px-4 text-slate-400 font-medium">Effective</th>
                        <th className="text-left py-3 px-4 text-slate-400 font-medium">Created</th>
                        <th className="text-left py-3 px-4 text-slate-400 font-medium">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allPolicies.map((policy, idx) => {
                        const isActive = currentPolicy?.id === policy.id;
                        return (
                          <tr 
                            key={policy.id || idx}
                            className={`border-b border-slate-800 ${isActive ? 'bg-green-900/20' : ''}`}
                          >
                            <td className="py-3 px-4">
                              <span className={isActive ? 'text-green-400 font-medium' : 'text-white'}>
                                {policy.credits_version_id}
                              </span>
                              {isActive && (
                                <span className="ml-2 text-xs bg-green-900/50 text-green-400 px-2 py-0.5 rounded">
                                  Active
                                </span>
                              )}
                            </td>
                            <td className="py-3 px-4 text-amber-400">{policy.aeu_to_credits_ratio}×</td>
                            <td className="py-3 px-4 text-cyan-400">{policy.peu_to_credits_ratio}×</td>
                            <td className="py-3 px-4 text-slate-300 text-xs">
                              {formatDate(policy.effective_start_timestamp)}
                            </td>
                            <td className="py-3 px-4 text-slate-500 text-xs">
                              {formatDate(policy.created_at)}
                            </td>
                            <td className="py-3 px-4 text-slate-400 text-xs max-w-xs truncate">
                              {policy.change_reason || '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-slate-400 text-center py-8">
                  No policy history available
                </div>
              )}
            </CardContent>
          </Card>
        )}
        </main>
      </div>

      <UniversalFooter />
    </div>
  );
};

export default AdminCreditsPage;
